import { AxiosResponse } from 'axios';
import { SET_POSITIONS, SET_POSITION, SET_ERRORMSG } from '@/store/mutation_types';
import {
  CLEAR_POSITION_ISLOADING,
  DELETE_POSITION,
  EDIT_POSITION,
  GET_POSITIONS,
  GET_POSITION,
  GET_POSITION_IMPACT,
  REGISTER_POSITION,
  TOGGLE_POSITION_IS_ACTIVE,
} from '@/store/action_types';
import Position from '@/services/position';
import { Module } from 'vuex';
import { EditPosition, RegisterPosition, GetPosition } from '@/dtos/position.dto';

/**
 * The axios interceptor pushes a human message into the error store for 4xx/5xx and
 * then resolves with `undefined`, so committing `result?.data?.msg` unconditionally
 * would overwrite that message with undefined.
 */
const reportError = (commit: any, result: any) => {
  const msg = result?.data?.msg;
  if (msg) commit(SET_ERRORMSG, msg);
};

interface Slot {
  data: any;
  isLoading: boolean;
  requestId: number;
}

const emptySlot = (): Slot => ({ data: null, isLoading: false, requestId: 0 });

const apply = (slot: Slot, payload: any) => {
  if (payload.requestId !== undefined && payload.requestId < slot.requestId) return;
  if (payload.requestId !== undefined) slot.requestId = payload.requestId;
  if ('data' in payload) slot.data = payload.data;
  slot.isLoading = Boolean(payload.isLoading);
};

const position: Module<any, {}> = {
  state: {
    position: emptySlot(),
    positions: emptySlot(),
  },
  getters: {
    position: (state: any) => state.position,
    positions: (state: any) => state.positions,
  },
  mutations: {
    [SET_POSITION]: (state: any, payload: any) => apply(state.position, payload),
    [SET_POSITIONS]: (state: any, payload: any) => apply(state.positions, payload),
  },
  actions: {
    /**
     * Every action here is wrapped: the axios interceptor REJECTS on a network drop
     * or timeout, and an unwrapped `await` left the loading flag stuck on forever
     * with the dialog frozen and no message.
     */
    [GET_POSITIONS]: async ({ commit, state }: any, payload: any) => {
      const requestId = state.positions.requestId + 1;
      // Cleared on purpose: the previously selected department's roles must not
      // linger while the new department's load is in flight.
      commit(SET_POSITIONS, { data: null, isLoading: true, requestId });

      try {
        const result: AxiosResponse = await Position.getPositions(payload as GetPosition);
        if (result?.data?.status) {
          commit(SET_POSITIONS, { data: result.data.data ?? [], isLoading: false, requestId });
          return true;
        }
        commit(SET_POSITIONS, { data: [], isLoading: false, requestId });
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_POSITIONS, { data: [], isLoading: false, requestId });
        return false;
      }
    },

    [GET_POSITION]: async ({ commit }: any, payload: any) => {
      commit(SET_POSITION, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Position.getPositions(payload as GetPosition);
        if (result?.data?.status) {
          commit(SET_POSITION, { data: result.data.data?.[0] ?? null, isLoading: false });
          return true;
        }
        commit(SET_POSITION, { data: null, isLoading: false });
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_POSITION, { data: null, isLoading: false });
        return false;
      }
    },

    [REGISTER_POSITION]: async ({ commit }: any, payload: any) => {
      commit(SET_POSITION, { isLoading: true });

      try {
        const result: AxiosResponse = await Position.registerPosition(payload as RegisterPosition);
        commit(SET_POSITION, { data: result?.data?.data ?? null, isLoading: false });
        if (result?.data?.status) return true;
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_POSITION, { isLoading: false });
        return false;
      }
    },

    [EDIT_POSITION]: async ({ commit }: any, payload: any) => {
      commit(SET_POSITION, { isLoading: true });

      try {
        const result: AxiosResponse = await Position.editPosition(payload as EditPosition);
        commit(SET_POSITION, { data: result?.data?.data ?? null, isLoading: false });
        if (result?.data?.status) return true;
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_POSITION, { isLoading: false });
        return false;
      }
    },

    [TOGGLE_POSITION_IS_ACTIVE]: async ({ commit }: any, payload: any) => {
      try {
        const result: AxiosResponse = await Position.togglePositionIsActive(payload);
        if (result?.data?.status) return true;
        reportError(commit, result);
        return false;
      } catch {
        return false;
      }
    },

    /** Resolves { title, employees } so a delete can warn with a real number. */
    [GET_POSITION_IMPACT]: async ({ commit }: any, payload: any) => {
      try {
        const result: AxiosResponse = await Position.getPositionImpact(payload);
        if (result?.data?.status) return result.data.data;
        reportError(commit, result);
        return null;
      } catch {
        return null;
      }
    },

    [DELETE_POSITION]: async ({ commit }: any, payload: string) => {
      commit(SET_POSITION, { isLoading: true });

      try {
        const result: AxiosResponse = await Position.deletePosition(payload);
        commit(SET_POSITION, { isLoading: false });
        if (result?.data?.status) return true;
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_POSITION, { isLoading: false });
        return false;
      }
    },

    [CLEAR_POSITION_ISLOADING]: ({ commit }: any) => {
      commit(SET_POSITION, { isLoading: false });
      commit(SET_POSITIONS, { isLoading: false });
    },
  },
};

export default position;
